import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import {
  PageContainer, StatCard, PremiumCard, DataTable, LoadingState,
} from "components/ui";

const IconClock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LEAVE_TYPE_LABEL = {
  PAID: "Paid", SICK: "Sick", UNPAID: "Unpaid", MATERNITY: "Maternity", OTHER: "Other",
};

const statusBadge = (status) => {
  const map = {
    PRESENT:  { bg: "rgba(16,185,129,0.15)", color: "#10b981" },
    LATE:     { bg: "rgba(245,158,11,0.15)", color: "#f59e0b" },
    ABSENT:   { bg: "rgba(239,68,68,0.15)",  color: "#ef4444" },
    ON_LEAVE: { bg: "rgba(99,102,241,0.15)", color: "#6366f1" },
  };
  const s = map[status] || { bg: "rgba(148,163,184,0.15)", color: "#94a3b8" };
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: "2px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
    }}>
      {status}
    </span>
  );
};

const RhDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate     = useNavigate();

  const [stats,           setStats]           = useState({ pendingCount: 0, presentToday: 0 });
  const [pendingLeaves,   setPendingLeaves]   = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [loading,         setLoading]         = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [pendingRes, attendanceRes] = await Promise.all([
          axiosPrivate.get("/api/rh/leave-requests/pending?page=0&size=5"),
          axiosPrivate.get("/api/rh/attendance/today?page=0&size=5"),
        ]);
        setPendingLeaves(pendingRes.data.content   || []);
        setTodayAttendance(attendanceRes.data.content || []);
        setStats({
          pendingCount: pendingRes.data.page?.totalElements   ?? 0,
          presentToday: attendanceRes.data.page?.totalElements ?? 0,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading HR overview…" /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>

      {/* ── KPI strip ──────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 16,
        marginBottom: 24,
      }}>
        <StatCard
          label="Pending approvals"
          value={stats.pendingCount}
          icon={<IconCheck />}
          accent={{ bg: "var(--warning-bg)", fg: "var(--warning)" }}
          onClick={() => navigate("/rh/leave-approvals")}
        />
        <StatCard
          label="Present today"
          value={stats.presentToday}
          icon={<IconClock />}
          accent={{ bg: "var(--success-bg)", fg: "var(--success)" }}
          onClick={() => navigate("/rh/attendance")}
        />
      </div>

      {/* ── Two-column previews ─────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

        {/* Pending leave requests */}
        <DataTable
          title="Pending leave requests"
          subtitle="Waiting for your review"
          data={pendingLeaves}
          rowKey={(r) => r.id}
          search={false}
          paginate={false}
          emptyTitle="No pending requests"
          headerAction={
            <button
              onClick={() => navigate("/rh/leave-approvals")}
              style={{ background: "none", border: "none", color: "#6366f1", fontSize: 12, cursor: "pointer" }}
            >
              View all →
            </button>
          }
          columns={[
            {
              key: "employee",
              header: "Employee",
              render: (r) => (
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{r.employeeName}</div>
                  <div className="t-mono t-muted" style={{ fontSize: 11 }}>
                    {LEAVE_TYPE_LABEL[r.leaveType]} · {r.startDate} → {r.endDate}
                  </div>
                </div>
              ),
            },
          ]}
        />

        {/* Today's attendance */}
        <DataTable
          title="Attendance today"
          subtitle={new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          data={todayAttendance}
          rowKey={(r) => r.id}
          search={false}
          paginate={false}
          emptyTitle="No clock-ins yet today"
          headerAction={
            <button
              onClick={() => navigate("/rh/attendance")}
              style={{ background: "none", border: "none", color: "#6366f1", fontSize: 12, cursor: "pointer" }}
            >
              View all →
            </button>
          }
          columns={[
            {
              key: "employee",
              header: "Employee",
              render: (r) => (
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{r.employeeName}</div>
                  <div className="t-mono t-muted" style={{ fontSize: 11 }}>
                    In: {r.clockIn ? r.clockIn.slice(11, 16) : "—"}
                    {r.clockOut ? ` · Out: ${r.clockOut.slice(11, 16)}` : ""}
                  </div>
                </div>
              ),
            },
            {
              key: "status",
              header: "Status",
              align: "right",
              render: (r) => statusBadge(r.status),
            },
          ]}
        />

      </div>
    </PageContainer>
  );
};

export default RhDashboard;